import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Textarea, Button, LanguageTabs, Loading } from '../components/ui';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface AboutContent {
  title_en: string;
  title_ro: string;
  bio1_en: string;
  bio1_ro: string;
  bio2_en: string;
  bio2_ro: string;
  bio3_en: string;
  bio3_ro: string;
  stats_title_en: string;
  stats_title_ro: string;
  worked_with_title_en: string;
  worked_with_title_ro: string;
  albums_title_en: string;
  albums_title_ro: string;
}

const AboutPage: React.FC = () => {
  const { data, loading, fetch, update } = useApi<AboutContent>('/api/content/about');
  const { showToast } = useToast();
  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [form, setForm] = useState<AboutContent>({
    title_en: '', title_ro: '',
    bio1_en: '', bio1_ro: '',
    bio2_en: '', bio2_ro: '',
    bio3_en: '', bio3_ro: '',
    stats_title_en: '', stats_title_ro: '',
    worked_with_title_en: '', worked_with_title_ro: '',
    albums_title_en: '', albums_title_ro: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    if (data) {
      setForm({
        title_en: data.title_en || '',
        title_ro: data.title_ro || '',
        bio1_en: data.bio1_en || '',
        bio1_ro: data.bio1_ro || '',
        bio2_en: data.bio2_en || '',
        bio2_ro: data.bio2_ro || '',
        bio3_en: data.bio3_en || '',
        bio3_ro: data.bio3_ro || '',
        stats_title_en: data.stats_title_en || '',
        stats_title_ro: data.stats_title_ro || '',
        worked_with_title_en: data.worked_with_title_en || '',
        worked_with_title_ro: data.worked_with_title_ro || '',
        albums_title_en: data.albums_title_en || '',
        albums_title_ro: data.albums_title_ro || '',
      });
    }
  }, [data]);

  const handleChange = (field: keyof AboutContent) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);
    if (result) {
      showToast('About section saved!', 'success');
    } else {
      showToast('Failed to save changes', 'error');
    }
  };

  if (loading && !data) return <Loading />;

  return (
    <>
      <PageHeader
        title="About Section"
        subtitle="Edit your bio and section titles"
        actions={<Button onClick={handleSave} loading={saving}>Save Changes</Button>}
      />

      <div className="admin-content">
        <Card title="Section Title">
          <LanguageTabs currentLang={lang} onChange={setLang} />
          <div className="admin-form">
            {lang === 'en' ? (
              <Input label="Section Title (EN)" value={form.title_en} onChange={handleChange('title_en')} />
            ) : (
              <Input label="Section Title (RO)" value={form.title_ro} onChange={handleChange('title_ro')} />
            )}
          </div>
        </Card>

        <div style={{ marginTop: 24 }}>
          <Card title="Biography">
            <LanguageTabs currentLang={lang} onChange={setLang} />
            <div className="admin-form">
              {lang === 'en' ? (
                <>
                  <Textarea label="Bio Paragraph 1 (EN)" value={form.bio1_en} onChange={handleChange('bio1_en')} rows={3} />
                  <Textarea label="Bio Paragraph 2 (EN)" value={form.bio2_en} onChange={handleChange('bio2_en')} rows={3} />
                  <Textarea label="Bio Paragraph 3 (EN)" value={form.bio3_en} onChange={handleChange('bio3_en')} rows={3} />
                </>
              ) : (
                <>
                  <Textarea label="Bio Paragraph 1 (RO)" value={form.bio1_ro} onChange={handleChange('bio1_ro')} rows={3} />
                  <Textarea label="Bio Paragraph 2 (RO)" value={form.bio2_ro} onChange={handleChange('bio2_ro')} rows={3} />
                  <Textarea label="Bio Paragraph 3 (RO)" value={form.bio3_ro} onChange={handleChange('bio3_ro')} rows={3} />
                </>
              )}
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Subsection Titles">
            <LanguageTabs currentLang={lang} onChange={setLang} />
            <div className="admin-form">
              {lang === 'en' ? (
                <>
                  <Input label="Stats Title (EN)" value={form.stats_title_en} onChange={handleChange('stats_title_en')} />
                  <Input label="Worked With Title (EN)" value={form.worked_with_title_en} onChange={handleChange('worked_with_title_en')} />
                  <Input label="Albums Title (EN)" value={form.albums_title_en} onChange={handleChange('albums_title_en')} />
                </>
              ) : (
                <>
                  <Input label="Stats Title (RO)" value={form.stats_title_ro} onChange={handleChange('stats_title_ro')} />
                  <Input label="Worked With Title (RO)" value={form.worked_with_title_ro} onChange={handleChange('worked_with_title_ro')} />
                  <Input label="Albums Title (RO)" value={form.albums_title_ro} onChange={handleChange('albums_title_ro')} />
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
